import { useRef, useState } from "react";
import { newIdempotencyKey, admin } from "../../api/client";
import { REFUND_MANUAL_REASON, type AdminOrderAction, type OrderSummary } from "../../api/types";
import { can, staffRole, useMe } from "../../auth/session";
import { Field, Icon, Modal, Pill, errorMessage, useToast } from "../../components/ui";
import { fmt, t } from "../../i18n";
import { brl } from "../../lib/money";

/** Motivo da exceção de um pedido, em texto de operador. */
export function exceptionReason(o: OrderSummary): { label: string; tone: "warn" | "crit"; detail: string } {
  const paid = o.pixCharge?.paidCents;
  if (needsManualRefund(o)) {
    return { label: t.toca.reasonRefundManual, tone: "crit", detail: fmt(t.toca.reasonRefundManualDetail, { amount: brl(paid ?? o.totalCents) }) };
  }
  if (o.state === "under_review" && (o.reviewReasons ?? []).includes("pix_amount_divergent")) {
    return { label: t.toca.reasonDivergent, tone: "crit", detail: paid ? `Recebido ${brl(paid)} · esperado ${brl(o.totalCents)}` : t.reviewReasons.pix_amount_divergent! };
  }
  if (paid && BigInt(paid) !== BigInt(o.totalCents)) {
    return { label: t.toca.reasonDivergent, tone: "crit", detail: `Recebido ${brl(paid)} · esperado ${brl(o.totalCents)}` };
  }
  if (o.state === "failed") {
    return { label: t.toca.reasonFailed, tone: "warn", detail: `Execução falhou no parceiro${o.execution?.failureCode ? ` (${o.execution.failureCode})` : ""} · fundos na IP` };
  }
  if (o.state === "under_review") {
    const reasons = (o.reviewReasons ?? []).map((r) => t.reviewReasons[r] ?? r);
    return { label: t.toca.reasonReview, tone: "warn", detail: reasons.join(" · ") || t.stateMessages.under_review! };
  }
  return { label: t.toca.reasonOther, tone: "warn", detail: o.stateLabel ?? o.state };
}

/** O parceiro não devolve sozinho e a Toca ainda não confirmou a devolução feita no dashboard. */
export function needsManualRefund(o: Pick<OrderSummary, "state" | "reviewReasons">): boolean {
  return (o.reviewReasons ?? []).includes(REFUND_MANUAL_REASON) && (o.state === "under_review" || o.state === "failed");
}

/** Ações disponíveis por estado e motivos (a API valida de novo). */
export function actionsFor(o: Pick<OrderSummary, "state" | "reviewReasons">): AdminOrderAction[] {
  const { state } = o;
  const base: AdminOrderAction[] = ["add_note"];
  if (needsManualRefund(o)) return ["confirm_manual_refund", ...base];
  if (state === "under_review") return ["approve_review", "reject_review", "start_refund", ...base];
  if (state === "failed") return ["reprocess", "start_refund", ...base];
  if (state === "funds_received" || state === "executing" || state === "sent") return ["reprocess", ...base];
  return base;
}

const ACTION_ICON: Record<AdminOrderAction, () => React.ReactNode> = {
  approve_review: Icon.check,
  reject_review: Icon.flag,
  start_refund: Icon.refund,
  reprocess: Icon.retry,
  add_note: Icon.pen,
  confirm_manual_refund: Icon.check,
};

export function OrderActions({ order, onDone, compact = false }: { order: Pick<OrderSummary, "id" | "state" | "reviewReasons">; onDone: () => void; compact?: boolean }) {
  const me = useMe();
  const role = staffRole(me);
  const toast = useToast();
  const [pick, setPick] = useState<AdminOrderAction | null>(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const keyRef = useRef<string>(newIdempotencyKey()); // uma chave por ação escolhida: retry após timeout não repete devolução/aprovação
  if (!can.actOnOrders(role)) return role === "readonly" || role === "compliance" ? <span className="muted small">{t.common.readonlyHint}</span> : null;
  const list = actionsFor(order);

  async function run() {
    if (!pick) return;
    setErr(null);
    if (!note.trim() && pick !== "reprocess") return setErr(t.toca.noteRequired);
    setBusy(true);
    try {
      await admin.orderAction(order.id, pick, note.trim() || undefined, keyRef.current);
      toast(fmt(t.toca.actionDone, { action: t.toca.actions[pick] }));
      keyRef.current = newIdempotencyKey();
      setPick(null);
      setNote("");
      onDone();
    } catch (e) {
      setErr(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="row" style={{ gap: 8 }}>
        {list.map((a) => (
          <button key={a} type="button" className={`btn ${a === "approve_review" || a === "confirm_manual_refund" ? "dark" : "ghost"} ${compact ? "xs" : "sm"}`} onClick={() => setPick(a)}>
            {ACTION_ICON[a]()}
            {t.toca.actions[a]}
          </button>
        ))}
      </div>
      {pick && (
        <Modal title={t.toca.actions[pick] ?? pick} onClose={() => !busy && setPick(null)}>
          <p className="ink2">{fmt(t.toca.actionConfirm, { action: t.toca.actions[pick] })} <span className="mono">{order.id}</span></p>
          {pick === "confirm_manual_refund" && <p className="ink2" style={{ fontSize: 13 }}>{t.toca.refundManualConfirmHint}</p>}
          <Field label={pick === "confirm_manual_refund" ? t.toca.refundManualRef : t.common.note}>
            {(id) => (
              <div className="inp">
                <textarea id={id} value={note} onChange={(e) => setNote(e.target.value)} placeholder={pick === "confirm_manual_refund" ? t.toca.refundManualRefPlaceholder : t.common.notePlaceholder} />
              </div>
            )}
          </Field>
          {err && <div className="form-error" role="alert">{err}</div>}
          <div className="row" style={{ justifyContent: "flex-end" }}>
            <button type="button" className="btn ghost" onClick={() => setPick(null)} disabled={busy}>{t.common.cancel}</button>
            <button type="button" className={`btn ${pick === "reject_review" || pick === "start_refund" ? "crit" : ""}`} onClick={run} disabled={busy}>{t.common.confirm}</button>
          </div>
        </Modal>
      )}
    </>
  );
}

export function KybStatusPill({ status }: { status: string }) {
  const tone = status === "approved" ? "ok" : status === "rejected" || status === "blocked" ? "crit" : status === "needs_more_info" ? "warn" : "mute";
  return <Pill tone={tone}>{t.toca.kybStatus[status] ?? status}</Pill>;
}

export function DecisionModal<D extends string>({
  title,
  subject,
  options,
  onDecide,
  onClose,
}: {
  title: string;
  subject: string;
  options: { value: D; label: string; danger?: boolean }[];
  onDecide: (d: D, note: string) => Promise<void>;
  onClose: () => void;
}) {
  const [decision, setDecision] = useState<D>(options[0]!.value);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  async function go() {
    setErr(null);
    if (!note.trim()) return setErr(t.toca.noteRequired);
    setBusy(true);
    try {
      await onDecide(decision, note.trim());
      onClose();
    } catch (e) {
      setErr(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }
  const chosen = options.find((o) => o.value === decision);
  return (
    <Modal title={title} onClose={() => !busy && onClose()}>
      <p className="ink2">{subject}</p>
      <div className="chips" role="radiogroup" aria-label={t.toca.kybDecide}>
        {options.map((o) => (
          <button key={o.value} type="button" className="chip" role="radio" aria-checked={decision === o.value} aria-pressed={decision === o.value} onClick={() => setDecision(o.value)}>
            {o.label}
          </button>
        ))}
      </div>
      <Field label={t.common.note}>
        {(id) => (
          <div className="inp">
            <textarea id={id} value={note} onChange={(e) => setNote(e.target.value)} placeholder={t.common.notePlaceholder} />
          </div>
        )}
      </Field>
      {err && <div className="form-error" role="alert">{err}</div>}
      <div className="row" style={{ justifyContent: "flex-end" }}>
        <button type="button" className="btn ghost" onClick={onClose} disabled={busy}>{t.common.cancel}</button>
        <button type="button" className={`btn ${chosen?.danger ? "crit" : ""}`} onClick={go} disabled={busy}>{t.common.confirm}</button>
      </div>
    </Modal>
  );
}
